import React, { useState, useEffect, useCallback } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import axios from 'axios';
import {
  GraduationCap, Calendar, MapPin, Users, Target, FileText, ExternalLink,
  X, Send, CheckCircle, BookOpen, ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_APP = {
  full_name: '', email: '', phone: '', organization: '',
  education_level: '', profile_type: 'individu', motivation: ''
};

const EMPTY_REQUEST = {
  requester_name: '', requester_email: '', requester_phone: '', organization: '',
  theme: '', number_of_participants: 10, location: '', requested_date: '', description: ''
};

const PROFILE_TYPES = [
  { id: 'animateur', label: "Animateur / formateur / facilitateur de terrain" },
  { id: 'leader', label: "Leader en transformation sociétale / personne d'influence" },
  { id: 'societe_civile', label: "Animateur de la société civile" },
  { id: 'universite', label: "Université / chercheur" },
  { id: 'individu', label: "Individu en transition professionnelle / reconversion" },
  { id: 'association', label: "Association paysanne / société civile" },
  { id: 'entreprise', label: "Entreprise" },
  { id: 'autre', label: "Autre" },
];

export default function FormationsPage() {
  const [tab, setTab] = useState('upcoming'); // upcoming | request
  const [formations, setFormations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyingTo, setApplyingTo] = useState(null);
  const [appData, setAppData] = useState(EMPTY_APP);
  const [submitting, setSubmitting] = useState(false);

  // Request flow state (Demande de formation)
  const [reqData, setReqData] = useState(EMPTY_REQUEST);
  const [reqLoading, setReqLoading] = useState(false);
  const [reqSubmitted, setReqSubmitted] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/formations`);
      setFormations(res.data);
    } catch (e) {
      console.error('Failed to load formations:', e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/formation-applications`, {
        formation_id: applyingTo.formation_id,
        ...appData
      });
      toast.success('Candidature envoyée. Vous recevrez une réponse par email.');
      setApplyingTo(null);
      setAppData(EMPTY_APP);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Erreur lors de l'envoi");
    } finally { setSubmitting(false); }
  };

  const handleChangeReq = (e) => {
    const { name, value } = e.target;
    setReqData({ ...reqData, [name]: value });
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    if (reqData.description.length > 500) {
      toast.error('La description ne doit pas dépasser 500 caractères');
      return;
    }
    setReqLoading(true);
    try {
      await axios.post(`${API}/training-requests`, {
        ...reqData,
        number_of_participants: parseInt(reqData.number_of_participants, 10)
      });
      setReqSubmitted(true);
      toast.success('Demande envoyée avec succès !');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors de la soumission');
    } finally { setReqLoading(false); }
  };

  const charsLeft = 500 - reqData.description.length;

  return (
    <div className="min-h-screen flex flex-col">
      <Toaster />
      <Navigation />

      <section className="section-padding bg-white" data-testid="formations-page">
        <div className="container mx-auto px-6">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#4A6B48]/10 mb-4">
              <GraduationCap className="text-[#4A6B48]" size={40} />
            </div>
            <h1
              className="text-4xl sm:text-5xl font-bold text-[#2E332D] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
              data-testid="formations-title"
            >
              Formations « Former Pour Transformer »
            </h1>
            <p className="text-base text-[#5C665A] max-w-4xl mx-auto leading-relaxed">
              L&apos;approche FPT est inclusive et ouverte à tous les acteurs de la communauté.
              Nos formations s&apos;adressent en premier lieu aux <strong>animateurs, formateurs, facilitateurs,
              et acteurs de terrain</strong> de tout bord appelés à accompagner le processus de la transformation
              sociétale en général et/ou <strong>toute personne en position d&apos;influence</strong> qui peut infuser
              une vision ou une attente concernant la participation d&apos;une communauté donnée. Y sont aussi inclus
              les <strong>animateurs de la société civile et les universités</strong>, les
              <strong> individus en transition professionnelle ou en reconversion</strong>, les
              <strong> associations paysannes</strong>, les <strong>entreprises</strong>.
              Enfin le tout dans la vision intégrale de CRESA.
            </p>
          </div>

          {/* Sub-tabs */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex rounded-lg border-2 border-[#E5E3DB] p-1 bg-white">
              <button
                onClick={() => setTab('upcoming')}
                data-testid="formations-tab-upcoming"
                className={`px-6 py-2 rounded-md text-sm font-bold transition-all inline-flex items-center gap-2 ${
                  tab === 'upcoming' ? 'bg-[#4A6B48] text-white' : 'text-[#5C665A] hover:text-[#4A6B48]'
                }`}
              >
                <BookOpen size={16} /> Formations à venir
              </button>
              <button
                onClick={() => setTab('request')}
                data-testid="formations-tab-request"
                className={`px-6 py-2 rounded-md text-sm font-bold transition-all inline-flex items-center gap-2 ${
                  tab === 'request' ? 'bg-[#4A6B48] text-white' : 'text-[#5C665A] hover:text-[#4A6B48]'
                }`}
              >
                <ClipboardList size={16} /> Demande de formation
              </button>
            </div>
          </div>

          {tab === 'upcoming' && (
            <>
              {loading && (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A6B48]"></div>
                </div>
              )}
              {!loading && formations.length === 0 && (
                <div className="text-center py-12" data-testid="no-formations-message">
                  <p className="text-[#5C665A]">Aucune formation publiée pour le moment. Revenez bientôt !</p>
                </div>
              )}
              {!loading && formations.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
                  {formations.map((f) => (
                    <article key={f.formation_id} className="card" data-testid={`formation-card-${f.formation_id}`}>
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className="text-xs font-bold px-2 py-1 rounded bg-[#4A6B48]/10 text-[#4A6B48] inline-flex items-center gap-1">
                          <Calendar size={12} /> {new Date(f.date).toLocaleDateString('fr-FR', { dateStyle: 'long' })}
                        </span>
                        {f.location && (
                          <span className="text-xs text-[#5C665A] inline-flex items-center gap-1">
                            <MapPin size={12} /> {f.location}
                          </span>
                        )}
                        <span className="text-xs font-bold px-2 py-1 rounded bg-[#D87D4A]/10 text-[#D87D4A]">
                          {f.modality}
                        </span>
                      </div>
                      <h2 className="text-xl font-bold text-[#2E332D] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                        {f.title}
                      </h2>
                      <p className="text-sm text-[#4A6B48] font-bold mb-3 inline-flex items-center gap-1">
                        <Target size={14} /> Thème : <span className="font-normal text-[#2E332D]">{f.theme}</span>
                      </p>
                      {f.description && <p className="text-sm text-[#5C665A] leading-relaxed mb-3">{f.description}</p>}
                      <div className="space-y-2 text-sm text-[#2E332D] mb-4">
                        <div className="flex items-start gap-2"><Users size={14} className="mt-0.5 text-[#4A6B48] shrink-0" /><div><strong>Participants attendus :</strong> {f.expected_participants}</div></div>
                        <div className="flex items-start gap-2"><FileText size={14} className="mt-0.5 text-[#4A6B48] shrink-0" /><div><strong>Profil candidat :</strong> {f.candidate_category}</div></div>
                        <div className="flex items-start gap-2"><FileText size={14} className="mt-0.5 text-[#D87D4A] shrink-0" /><div><strong>Conditions :</strong> <span className="whitespace-pre-wrap">{f.conditions}</span></div></div>
                        <div className="flex items-start gap-2"><Target size={14} className="mt-0.5 text-[#D87D4A] shrink-0" /><div><strong>Résultats attendus :</strong> <span className="whitespace-pre-wrap">{f.expected_results}</span></div></div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => setApplyingTo(f)} data-testid={`apply-formation-${f.formation_id}`} className="btn-primary inline-flex items-center">
                          Postuler en ligne
                        </button>
                        {f.google_form_url && (
                          <a href={f.google_form_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-4 py-2 border-2 border-[#4A6B48] text-[#4A6B48] rounded-md hover:bg-[#4A6B48] hover:text-white transition-all text-sm font-bold">
                            <ExternalLink size={14} /> Formulaire Google
                          </a>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </>
          )}

          {tab === 'request' && (
            <div className="max-w-2xl mx-auto" data-testid="request-tab-content">
              {reqSubmitted ? (
                <div className="card text-center py-12" data-testid="training-success">
                  <CheckCircle className="text-[#4A6B48] mx-auto mb-4" size={64} />
                  <h2 className="text-2xl font-bold text-[#2E332D] mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    Demande envoyée !
                  </h2>
                  <p className="text-[#5C665A] mb-6">
                    Merci pour votre demande de formation. Notre équipe va l&apos;étudier et vous contactera
                    prochainement pour discuter des détails et de la faisabilité.
                  </p>
                  <button onClick={() => { setReqSubmitted(false); setReqData(EMPTY_REQUEST); }} className="btn-primary">
                    Faire une autre demande
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmitRequest} className="card" data-testid="training-form">
                  <p className="text-sm text-[#5C665A] mb-4">
                    Soumettez votre demande de formation sur mesure pour votre organisation ou votre communauté.
                  </p>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-[#2E332D] mb-1">Nom complet *</label>
                        <input type="text" name="requester_name" required value={reqData.requester_name} onChange={handleChangeReq} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="training-name-input" />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-[#2E332D] mb-1">Email *</label>
                        <input type="email" name="requester_email" required value={reqData.requester_email} onChange={handleChangeReq} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="training-email-input" />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-[#2E332D] mb-1">Téléphone</label>
                        <input type="tel" name="requester_phone" value={reqData.requester_phone} onChange={handleChangeReq} placeholder="+243 XXX XXX XXX" className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-[#2E332D] mb-1">Organisation</label>
                        <input type="text" name="organization" value={reqData.organization} onChange={handleChangeReq} placeholder="ONG, école, communauté..." className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-[#2E332D] mb-1 flex items-center gap-2">
                        <FileText size={16} /> Thème de la formation *
                      </label>
                      <input type="text" name="theme" required value={reqData.theme} onChange={handleChangeReq} placeholder="Ex: Agroforesterie, Citoyenneté participative..." className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="training-theme-input" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-[#2E332D] mb-1 flex items-center gap-1">
                          <Users size={14} /> Nb participants *
                        </label>
                        <input type="number" name="number_of_participants" required min="1" value={reqData.number_of_participants} onChange={handleChangeReq} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="training-participants-input" />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-[#2E332D] mb-1 flex items-center gap-1">
                          <MapPin size={14} /> Lieu *
                        </label>
                        <input type="text" name="location" required value={reqData.location} onChange={handleChangeReq} placeholder="Ville, Province" className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="training-location-input" />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-[#2E332D] mb-1 flex items-center gap-1">
                          <Calendar size={14} /> Date souhaitée *
                        </label>
                        <input type="date" name="requested_date" required value={reqData.requested_date} onChange={handleChangeReq} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="training-date-input" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-[#2E332D] mb-1">
                        Description de la formation *
                        <span className={`ml-2 text-xs font-normal ${charsLeft < 50 ? 'text-red-500' : 'text-[#5C665A]'}`}>({charsLeft}/500)</span>
                      </label>
                      <textarea name="description" required rows={5} maxLength={500} value={reqData.description} onChange={handleChangeReq} placeholder="Décrivez vos besoins, objectifs et contexte..." className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="training-description-input" />
                    </div>
                    <button type="submit" disabled={reqLoading} className="w-full btn-primary py-3 inline-flex items-center justify-center disabled:opacity-50" data-testid="submit-training-button">
                      <Send size={18} className="mr-2" />
                      {reqLoading ? 'Envoi en cours...' : 'Envoyer la demande'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Application modal */}
      {applyingTo && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4" style={{ zIndex: 9999 }} data-testid="apply-modal">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 shadow-2xl">
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-xs text-[#5C665A]">Candidature pour la formation :</p>
                <h2 className="text-xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {applyingTo.title}
                </h2>
              </div>
              <button onClick={() => setApplyingTo(null)} className="p-1 hover:bg-gray-100 rounded"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmitApplication} data-testid="apply-formation-form">
              <div className="space-y-3">
                <input type="text" required placeholder="Nom complet *" value={appData.full_name} onChange={(e) => setAppData({ ...appData, full_name: e.target.value })} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="apply-name-input" />
                <input type="email" required placeholder="Email *" value={appData.email} onChange={(e) => setAppData({ ...appData, email: e.target.value })} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="apply-email-input" />
                <input type="tel" placeholder="Téléphone" value={appData.phone} onChange={(e) => setAppData({ ...appData, phone: e.target.value })} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />
                <input type="text" placeholder="Organisation (si applicable)" value={appData.organization} onChange={(e) => setAppData({ ...appData, organization: e.target.value })} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />
                <select value={appData.profile_type} onChange={(e) => setAppData({ ...appData, profile_type: e.target.value })} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="apply-profile-select">
                  {PROFILE_TYPES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
                <input type="text" placeholder="Niveau d'étude (ou « sans niveau »)" value={appData.education_level} onChange={(e) => setAppData({ ...appData, education_level: e.target.value })} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />
                <textarea required rows={4} placeholder="Motivation (pourquoi cette formation ? max 500 caractères) *" maxLength={500} value={appData.motivation} onChange={(e) => setAppData({ ...appData, motivation: e.target.value })} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="apply-motivation-input" />
              </div>
              <div className="flex gap-2 mt-4 justify-end">
                <button type="button" onClick={() => setApplyingTo(null)} className="px-4 py-2 border border-[#E5E3DB] rounded-lg text-[#5C665A] hover:bg-gray-50">Annuler</button>
                <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50" data-testid="submit-application-button">
                  {submitting ? 'Envoi...' : 'Envoyer ma candidature'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
